import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePermissions from "../hooks/usePermissions";

const API = process.env.REACT_APP_API_URL || "/api";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  /* 🔥 RBAC */
  const {
    canView,
    canCreate,
    canEdit,
    canDelete,
  } = usePermissions();

  /* ================= FETCH ================= */
  const fetchProducts = useCallback(async () => {
    try {
      setError("");
      const res = await fetch(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch products");

      const safeData = Array.isArray(data)
        ? data
        : data.rows || data.data || [];

      setProducts(safeData);

    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message || "Failed to fetch products");
      setProducts([]);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchProducts();
  }, [token, fetchProducts]);

  /* ================= CREATE ================= */
  const createProduct = async () => {
    const productName = name.trim();
    if (!productName || !canCreate("products") || saving) return;

    try {
      setSaving(true);
      setError("");

      const res = await fetch(`${API}/products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: productName }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to add product");

      setName("");
      fetchProducts();

    } catch (err) {
      console.error("Create error:", err);
      setError(err.message || "Failed to add product");
    } finally {
      setSaving(false);
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!canDelete("products")) return;

    if (!window.confirm("Delete this product?")) return;

    try {
      const res = await fetch(`${API}/products/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Delete failed");

      fetchProducts();

    } catch (err) {
      alert(err.message);
    }
  };

  /* ================= UI ================= */

  /* 🔒 PAGE ACCESS */
  if (!canView("products")) {
    return <div className="p-6 text-red-500">Access Denied</div>;
  }

  return (
    <div className="space-y-5">

      <section className="cb-hero-panel rounded-lg px-6 py-5 text-white">
        <p className="text-xs font-bold uppercase text-emerald-100">Device Blueprints</p>
        <div className="mt-1 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight">Products</h1>
            <p className="mt-2 max-w-2xl text-sm font-medium text-emerald-50/85">
              Configure reusable sensor and relay templates for every device model.
            </p>
          </div>
          <div className="rounded-lg border border-white/15 bg-white/10 px-4 py-3 text-sm font-bold">
            {products.length} product types
          </div>
        </div>
      </section>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* 🔥 CREATE */}
      {canCreate("products") && (
        <div className="cb-panel flex flex-wrap gap-3 rounded-lg p-4">
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") createProduct();
            }}
            className="min-w-[260px] flex-1 border px-3 py-2 text-sm"
            placeholder="New product name"
          />

          <button
            onClick={createProduct}
            disabled={saving || !name.trim()}
            className="rounded-lg bg-green-600 px-5 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Adding..." : "Add"}
          </button>
        </div>
      )}

      {/* LIST */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {products.map((p) => (
          <div
            key={p.id}
            className="cb-stat-card rounded-lg p-5 transition hover:-translate-y-0.5"
          >
            <div
              onClick={() =>
                navigate(`/dashboard/products/${p.id}`)
              }
              className="cursor-pointer"
            >
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-sm font-black text-emerald-700">
                {String(p.name || "P").slice(0, 1).toUpperCase()}
              </div>
              <h2 className="text-lg font-black text-slate-950">{p.name}</h2>
              <p className="mt-1 text-sm font-medium text-gray-500">
                Configure sensors & relays
              </p>
            </div>

            {/* 🔥 ACTIONS */}
            <div className="mt-5 flex gap-3 text-sm">

              {canEdit("products") && (
                <button
                  onClick={() =>
                    navigate(`/dashboard/products/${p.id}`)
                  }
                  className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700"
                >
                  Edit
                </button>
              )}

              {canDelete("products") && (
                <button
                  onClick={() => handleDelete(p.id)}
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600"
                >
                  Delete
                </button>
              )}

            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
